/**
 * Persistência no browser escopada por workspace (schema slug).
 * Prefixo: xpro:ws:{slug}:{baseKey}
 *
 * Auth (bearerToken, userData), tema e locale permanecem globais.
 * O schema ativo da ABA fica em sessionStorage (`xpro:tab:workspaceDatabase`) —
 * assim várias abas podem usar workspaces diferentes sem misturar o header Company.
 */

export const WS_KEY = {
  SELECTED_COMPANIES: "selectedCompanies",
  ACTIVE_COMPANY: "activeCompany",
  SELECTED_COMPANY: "selectedCompany",
  ACTIVE_PROJECT_ID: "activeProjectId",
  DASHBOARD_START: "dashboard_start_date",
  DASHBOARD_END: "dashboard_end_date",
  DASHBOARD_ADVANCED_FILTERS: "dashboard_advanced_filters",
  DASHBOARD_CAMPAIGN_FILTERS: "dashboard_campaign_filters",
  DASHBOARD_SALES_FILTERS: "dashboard_sales_filters",
  DASHBOARD_CLIENTS_FILTERS: "dashboard_clients_filters",
  DASHBOARD_PRODUTOS_FILTERS: "dashboard_produtos_filters",
  DASHBOARD_PASSAGENS_FILTERS: "dashboard_passagens_filters",
  DASHBOARD_SURVEYS_FILTERS: "dashboard_surveys_filters",
  DASHBOARD_SAVED_FILTERS: "dashboard_saved_filters",
  PERM_SCOPES_CACHE: "perm_scopes_cache",
  WORKSPACE_CONFIG_CACHE: "workspace-config-cache",
  CHAT_FILTERS: "xpro_chat_filters",
  CHAT_ADVANCED_FILTERS: "xpro_chat_advanced_filters",
  SAVED_FILTERS: "xpro_saved_filters",
  CHAT_SEARCH: "xpro_chat_search",
  CHAT_TIMELINE_FILTERS: "xpro_chat_timeline_filters",
  CHAT_LAST_SEEN: "xpro_chat_last_seen_client_at",
  CHAT_QUEUE_KEYBOARD_HINT_DISMISSED: "xpro_chat_queue_keyboard_hint_dismissed",
  WHATSAPP_GROUP_MENTION_HINT_SEEN: "whatsapp_group_mention_hint_seen",
  COLLEAGUE_MENTION_HINT_SEEN: "colleague_mention_hint_seen",
  SEARCH_HISTORY: "searchHistory",
  SEARCH_RECENT_USERS: "searchRecentUsers",
  SEARCH_RECENT_CLIENTS: "searchRecentClients",
  PLATFORM_AI_CHAT: "platform_ai_chat",
  PRODUCT_TABLE_VISIBLE: "productTable.visibleColumns",
  PRODUCT_TABLE_VISIBLE_PASSENGER: "productTable.visibleColumnsPassenger",
  PRODUCT_TABLE_WIDTHS: "productTable.columnWidths",
  USER_STATUS: "status",
  FUTURE_CONTACT_DISMISSED: "xpro_dismissed_future_contact_ids",
  SCHEDULE_REMINDER_DISMISSED: "xpro_dismissed_schedule_reminder_ids",
  SCHEDULE_REMINDER_NOTIFIED: "xpro_notified_schedule_reminder_keys",
  HYGIENE_ACTIVE_JOB: "client_hygiene_active_job",
  CENTRAL_LINKED_IMPORT_ACTIVE_JOB: "central_linked_import_active_job",
  CONCESSION_IMPORT_MAP: "concession_import_map",
};

const PREFIX = "xpro:ws:";

/**
 * sessionStorage por aba: schema MySQL ativo nesta aba (não compartilhado entre abas).
 * Tem prioridade sobre `localStorage.company` no header Company e sockets.
 */
export const TAB_WORKSPACE_DATABASE_KEY = "xpro:tab:workspaceDatabase";

/** sessionStorage — chaves globais legadas (pré-escopo por workspace). */
const LEGACY_SESSION_KEYS = new Set([
  WS_KEY.SELECTED_COMPANIES,
  WS_KEY.ACTIVE_COMPANY,
  WS_KEY.SELECTED_COMPANY,
  WS_KEY.PERM_SCOPES_CACHE,
  WS_KEY.WORKSPACE_CONFIG_CACHE,
  WS_KEY.USER_STATUS,
  WS_KEY.SEARCH_HISTORY,
  WS_KEY.DASHBOARD_START,
  WS_KEY.DASHBOARD_END,
]);

/** localStorage — chaves globais legadas. */
const LEGACY_LOCAL_KEYS = new Set([
  WS_KEY.ACTIVE_PROJECT_ID,
  WS_KEY.PERM_SCOPES_CACHE,
  WS_KEY.PRODUCT_TABLE_VISIBLE,
  WS_KEY.PRODUCT_TABLE_VISIBLE_PASSENGER,
  WS_KEY.PRODUCT_TABLE_WIDTHS,
  WS_KEY.DASHBOARD_START,
  WS_KEY.DASHBOARD_END,
]);

export function normalizeWorkspaceSlug(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!s || !/^[a-z0-9_]{1,64}$/.test(s)) return null;
  if (s === "xprobase") return null;
  return s;
}

function readLocalWorkspaceDatabaseSlug() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem("company");
    const s = String(raw ?? "").trim();
    if (!s || /^\d+$/.test(s)) {
      return normalizeWorkspaceSlug(localStorage.getItem("xproWorkspaceDatabase"));
    }
    return normalizeWorkspaceSlug(s);
  } catch {
    return null;
  }
}

export function readTabWorkspaceDatabase() {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return normalizeWorkspaceSlug(sessionStorage.getItem(TAB_WORKSPACE_DATABASE_KEY));
  } catch {
    return null;
  }
}

/** Fixa o schema desta aba (sessionStorage). Não altera outras abas. */
export function writeTabWorkspaceDatabase(slug) {
  const normalized = normalizeWorkspaceSlug(slug);
  try {
    if (typeof sessionStorage === "undefined") return normalized;
    if (!normalized) {
      sessionStorage.removeItem(TAB_WORKSPACE_DATABASE_KEY);
      return null;
    }
    sessionStorage.setItem(TAB_WORKSPACE_DATABASE_KEY, normalized);
    return normalized;
  } catch {
    return normalized;
  }
}

export function clearTabWorkspaceDatabase() {
  writeTabWorkspaceDatabase(null);
}

/**
 * Na 1ª carga da aba, congela o schema atual (localStorage ou ?ws=) nesta aba.
 * Depois disso, mudanças de `company` em outras abas não afetam o header desta.
 */
export function ensureTabWorkspaceDatabasePinned() {
  const existing = readTabWorkspaceDatabase();
  if (existing) return existing;
  const fromLocal = readLocalWorkspaceDatabaseSlug();
  if (!fromLocal) return null;
  return writeTabWorkspaceDatabase(fromLocal);
}

/**
 * Ativa o schema do tenant nesta aba + espelho em localStorage (último workspace usado).
 * Preferir esta função a gravar só em localStorage quando a troca for intencional nesta aba.
 */
export function setActiveWorkspaceDatabase(slug) {
  const normalized = normalizeWorkspaceSlug(slug);
  if (!normalized) return null;
  writeTabWorkspaceDatabase(normalized);
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("company", normalized);
      localStorage.setItem("xproWorkspaceDatabase", normalized);
    }
  } catch {
    /* ignore */
  }
  return normalized;
}

export function readWorkspaceSlugFromUrl(search) {
  try {
    const q =
      search != null
        ? String(search)
        : typeof window !== "undefined"
          ? window.location.search || ""
          : "";
    const params = new URLSearchParams(q.startsWith("?") ? q.slice(1) : q);
    // `c` = identificador da empresa na tela de login (mesmo slug de tenant que `ws`).
    return normalizeWorkspaceSlug(
      params.get("ws") || params.get("company") || params.get("c")
    );
  } catch {
    return null;
  }
}

/** Slug do tenant ativo nesta aba: URL `?ws=` → pin session → localStorage. */
export function getActiveWorkspaceSlug() {
  const fromUrl = readWorkspaceSlugFromUrl();
  if (fromUrl) {
    // Alinha o pin da aba à URL (fonte de verdade desta aba após open/reload).
    try {
      if (readTabWorkspaceDatabase() !== fromUrl) {
        writeTabWorkspaceDatabase(fromUrl);
      }
    } catch {
      /* ignore */
    }
    return fromUrl;
  }
  return readTabWorkspaceDatabase() || readLocalWorkspaceDatabaseSlug();
}

export function buildWorkspaceStorageKey(baseKey, workspaceSlug) {
  const slug = normalizeWorkspaceSlug(workspaceSlug) || getActiveWorkspaceSlug();
  if (!slug || !baseKey) return null;
  return `${PREFIX}${slug}:${baseKey}`;
}

function migrateLegacy(store, scopedKey, legacyKeys) {
  if (!scopedKey || !store) return null;
  try {
    const hit = store.getItem(scopedKey);
    if (hit != null) return hit;
    for (const leg of legacyKeys) {
      const v = store.getItem(leg);
      if (v != null) {
        store.setItem(scopedKey, v);
        store.removeItem(leg);
        return v;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function sessionStore() {
  return typeof sessionStorage !== "undefined" ? sessionStorage : null;
}

function localStore() {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

export function readWorkspaceSession(baseKey, workspaceSlug) {
  const store = sessionStore();
  const scoped = buildWorkspaceStorageKey(baseKey, workspaceSlug);
  if (!store || !scoped) return null;
  return migrateLegacy(store, scoped, [baseKey]);
}

export function writeWorkspaceSession(baseKey, value, workspaceSlug) {
  const store = sessionStore();
  const scoped = buildWorkspaceStorageKey(baseKey, workspaceSlug);
  if (!store || !scoped) return;
  try {
    store.setItem(scoped, value);
    if (LEGACY_SESSION_KEYS.has(baseKey)) store.removeItem(baseKey);
  } catch {
    /* ignore */
  }
}

export function removeWorkspaceSession(baseKey, workspaceSlug) {
  const store = sessionStore();
  const scoped = buildWorkspaceStorageKey(baseKey, workspaceSlug);
  if (!store || !scoped) return;
  try {
    store.removeItem(scoped);
    if (LEGACY_SESSION_KEYS.has(baseKey)) store.removeItem(baseKey);
  } catch {
    /* ignore */
  }
}

export function readWorkspaceLocal(baseKey, workspaceSlug) {
  const store = localStore();
  const scoped = buildWorkspaceStorageKey(baseKey, workspaceSlug);
  if (!store || !scoped) return null;
  return migrateLegacy(store, scoped, [baseKey]);
}

export function writeWorkspaceLocal(baseKey, value, workspaceSlug) {
  const store = localStore();
  const scoped = buildWorkspaceStorageKey(baseKey, workspaceSlug);
  if (!store || !scoped) return;
  try {
    store.setItem(scoped, value);
    if (LEGACY_LOCAL_KEYS.has(baseKey)) store.removeItem(baseKey);
  } catch {
    /* ignore */
  }
}

export function removeWorkspaceLocal(baseKey, workspaceSlug) {
  const store = localStore();
  const scoped = buildWorkspaceStorageKey(baseKey, workspaceSlug);
  if (!store || !scoped) return;
  try {
    store.removeItem(scoped);
    if (LEGACY_LOCAL_KEYS.has(baseKey)) store.removeItem(baseKey);
  } catch {
    /* ignore */
  }
}

export function readWorkspaceSessionJson(baseKey, fallback, workspaceSlug) {
  const raw = readWorkspaceSession(baseKey, workspaceSlug);
  if (raw == null || raw === "") return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function writeWorkspaceSessionJson(baseKey, value, workspaceSlug) {
  writeWorkspaceSession(baseKey, JSON.stringify(value), workspaceSlug);
}

export function readWorkspaceLocalJson(baseKey, fallback, workspaceSlug) {
  const raw = readWorkspaceLocal(baseKey, workspaceSlug);
  if (raw == null || raw === "") return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function writeWorkspaceLocalJson(baseKey, value, workspaceSlug) {
  writeWorkspaceLocal(baseKey, JSON.stringify(value), workspaceSlug);
}

/** IDs de projetos selecionados no seletor de empresas. */
export function getSelectedCompanyIds(workspaceSlug) {
  const arr = readWorkspaceSessionJson(WS_KEY.SELECTED_COMPANIES, [], workspaceSlug);
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
}

export function setSelectedCompanyIds(ids, workspaceSlug) {
  const norm = Array.isArray(ids)
    ? ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  writeWorkspaceSessionJson(WS_KEY.SELECTED_COMPANIES, norm, workspaceSlug);
}

export function readActiveProjectId(workspaceSlug) {
  const raw = readWorkspaceLocal(WS_KEY.ACTIVE_PROJECT_ID, workspaceSlug);
  if (raw == null || raw === "") return null;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function writeActiveProjectId(id, workspaceSlug) {
  if (id == null || id === "") {
    removeWorkspaceLocal(WS_KEY.ACTIVE_PROJECT_ID, workspaceSlug);
    return;
  }
  writeWorkspaceLocal(WS_KEY.ACTIVE_PROJECT_ID, String(id), workspaceSlug);
}

export function readActiveCompanySession(workspaceSlug) {
  return readWorkspaceSession(WS_KEY.ACTIVE_COMPANY, workspaceSlug);
}

export function writeActiveCompanySession(value, workspaceSlug) {
  if (value == null || value === "") {
    removeWorkspaceSession(WS_KEY.ACTIVE_COMPANY, workspaceSlug);
    return;
  }
  writeWorkspaceSession(WS_KEY.ACTIVE_COMPANY, String(value), workspaceSlug);
}

export function readSelectedCompanySession(workspaceSlug) {
  return readWorkspaceSession(WS_KEY.SELECTED_COMPANY, workspaceSlug);
}

export function readDashboardDate(which, workspaceSlug) {
  const key = which === "start" ? WS_KEY.DASHBOARD_START : WS_KEY.DASHBOARD_END;
  let v = readWorkspaceSession(key, workspaceSlug);
  if (v != null) return v;
  const ls = localStore();
  if (ls) {
    try {
      const leg = ls.getItem(key);
      if (leg != null) {
        writeWorkspaceSession(key, leg, workspaceSlug);
        ls.removeItem(key);
        return leg;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function writeDashboardDate(which, ymd, workspaceSlug) {
  const key = which === "start" ? WS_KEY.DASHBOARD_START : WS_KEY.DASHBOARD_END;
  writeWorkspaceSession(key, ymd, workspaceSlug);
}

export function readDashboardAdvancedFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_ADVANCED_FILTERS, null, workspaceSlug);
}

export function writeDashboardAdvancedFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_ADVANCED_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_ADVANCED_FILTERS, value, workspaceSlug);
}

export function readDashboardCampaignFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_CAMPAIGN_FILTERS, null, workspaceSlug);
}

export function writeDashboardCampaignFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_CAMPAIGN_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_CAMPAIGN_FILTERS, value, workspaceSlug);
}

export function readDashboardSalesFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_SALES_FILTERS, null, workspaceSlug);
}

export function writeDashboardSalesFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_SALES_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_SALES_FILTERS, value, workspaceSlug);
}

export function readDashboardClientsFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_CLIENTS_FILTERS, null, workspaceSlug);
}

export function writeDashboardClientsFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_CLIENTS_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_CLIENTS_FILTERS, value, workspaceSlug);
}

export function readDashboardProdutosFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_PRODUTOS_FILTERS, null, workspaceSlug);
}

export function writeDashboardProdutosFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_PRODUTOS_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_PRODUTOS_FILTERS, value, workspaceSlug);
}

export function readDashboardPassagensFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_PASSAGENS_FILTERS, null, workspaceSlug);
}

export function writeDashboardPassagensFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_PASSAGENS_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_PASSAGENS_FILTERS, value, workspaceSlug);
}

export function readDashboardSurveysFilters(workspaceSlug) {
  return readWorkspaceLocalJson(WS_KEY.DASHBOARD_SURVEYS_FILTERS, null, workspaceSlug);
}

export function writeDashboardSurveysFilters(value, workspaceSlug) {
  if (value == null) {
    removeWorkspaceLocal(WS_KEY.DASHBOARD_SURVEYS_FILTERS, workspaceSlug);
    return;
  }
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_SURVEYS_FILTERS, value, workspaceSlug);
}

/** Chave dinâmica (ex.: selectedChannel123) escopada ao workspace. */
export function readWorkspaceSessionDynamic(suffix, workspaceSlug) {
  return readWorkspaceSession(suffix, workspaceSlug);
}

export function writeWorkspaceSessionDynamic(suffix, value, workspaceSlug) {
  writeWorkspaceSession(suffix, value, workspaceSlug);
}

function collectScopedKeys(store, workspaceSlug) {
  const keys = [];
  if (!store) return keys;
  const slug = workspaceSlug ? normalizeWorkspaceSlug(workspaceSlug) : null;
  const prefix = slug ? `${PREFIX}${slug}:` : PREFIX;
  try {
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
  } catch {
    /* ignore */
  }
  return keys;
}

/** Remove todas as chaves `xpro:ws:*` (logout) ou só de um workspace. */
export function clearWorkspaceScopedStorage(workspaceSlug) {
  for (const store of [sessionStore(), localStore()]) {
    if (!store) continue;
    for (const k of collectScopedKeys(store, workspaceSlug)) {
      try {
        store.removeItem(k);
      } catch {
        /* ignore */
      }
    }
  }
}

/** Remove chaves globais legadas que vazavam entre workspaces. */
export function clearLegacyGlobalWorkspaceKeys() {
  const ss = sessionStore();
  const ls = localStore();
  if (ss) {
    for (const k of LEGACY_SESSION_KEYS) {
      try {
        ss.removeItem(k);
      } catch {
        /* ignore */
      }
    }
  }
  if (ls) {
    for (const k of LEGACY_LOCAL_KEYS) {
      try {
        ls.removeItem(k);
      } catch {
        /* ignore */
      }
    }
  }
}

export function purgeWorkspaceBrowserStorageOnLogout() {
  clearWorkspaceScopedStorage();
  clearLegacyGlobalWorkspaceKeys();
  // Evita que a próxima tentativa de login na mesma aba herde o pin antigo
  // (localStorage.company muda, mas o header/API continuariam no tenant anterior).
  clearTabWorkspaceDatabase();
}
